import type { PetitionDraft } from '#src/app/shared/api/petitionDraft';

import type { machine as chatAgentActor } from './actors/chatAgent/actor';
import type { machine as controlActor } from './actors/control/actor';
import type { machine as editVariantAActor } from './actors/editVariantA/actor';
import type { machine as editVariantBActor } from './actors/editVariantB/actor';

export type Draft = Partial<PetitionDraft>;

export const MediaTypes = { AI: 'AI_GENERATED', USER: 'USER_UPLOADED' } as const;

export type StateInput = object;

export type StateContext = { draft?: Draft | null };

export type StateEvents =
	| { type: 'back'; target?: null }
	| { type: 'submit'; draft?: Draft; target?: string | null }
	// `reason` discriminates how the error was raised so a state can route on it; absent
	// means the default (draft-generation) failure. Extend the union as new sources are added.
	| { type: 'error'; reason?: 'load_timeout' }
	| { type: 'sync_draft'; draft: Draft | null };

export type StateActions = {
	syncDraft: () => void;
	onSubmit: () => void;
	onError: () => void;
	onLoadTimeout: () => void;
	// TODO: these actions should be removed after separating the control machine
	persistDraftType: () => void;
	unsetDraftType: () => void;
};

export type StateGuards = {
	isControl: (args: { context: StateContext; event: StateEvents }, params?: unknown) => boolean;
	isEditVariantA: (args: { context: StateContext; event: StateEvents }, params?: unknown) => boolean;
	isEditVariantB: (args: { context: StateContext; event: StateEvents }, params?: unknown) => boolean;
	isChatAgent: (args: { context: StateContext; event: StateEvents }, params?: unknown) => boolean;
	// TODO: these guards should be removed after separating the control machine
	transitionTo: (_: unknown, params: { target: string }) => boolean;
	isUserDraft: (args: { context: StateContext; event: StateEvents }, params?: unknown) => boolean;
	isAiDraft: (args: { context: StateContext; event: StateEvents }, params?: unknown) => boolean;
	didOptOut: (args: { context: StateContext; event: StateEvents }, params?: unknown) => boolean;
	isChatFlow: (args: { context: StateContext; event: StateEvents }, params?: unknown) => boolean;
	isLoadTimeout: (args: { context: StateContext; event: StateEvents }, params?: unknown) => boolean;
	isLoadTimeoutWithIssue: (args: { context: StateContext; event: StateEvents }, params?: unknown) => boolean;
};

export type StateChildren = {
	controlActorId: 'controlActorSrc';
	editVariantAActorId: 'editVariantAActorSrc';
	editVariantBActorId: 'editVariantBActorSrc';
	chatAgentActorId: 'chatAgentActorSrc';
};

export type StateActors = {
	controlActorSrc: typeof controlActor;
	editVariantAActorSrc: typeof editVariantAActor;
	editVariantBActorSrc: typeof editVariantBActor;
	chatAgentActorSrc: typeof chatAgentActor;
};
